import React from 'react';

import { TextField } from 'Containers/App/types';
import { useTextContext } from 'Contexts/textContext';
import { H6, Paragraph } from 'DesignLibrary/atoms';
import Plan from 'Models/plan';
import RichText from 'Shared/RichText';
import Text from 'Shared/Text';
import { NetworkTier, RxTierNames } from 'Types/entities';
import { getRxFieldAndVars, getRxTierName, isDeductibleWaived } from 'Utils/helpers';

import { CompareTable } from '../_shared/CompareTable';
import { CompareTableRow } from '../_shared/CompareTableRow';

export interface RxCoverageProps {
  plans: Plan[];
  getSelectedTier?: (plan: Plan) => NetworkTier;
}

export const RxCoverage = ({ plans, getSelectedTier }: RxCoverageProps) => {
  const { retrieveContentfulData } = useTextContext();

  if (!plans.length) {
    return null; // Prevent rendering if no plans exist
  }

  const header = retrieveContentfulData<string>(
    'health_details_section.rx_tiers_static_values.coverage_heading',
  );
  const rxHeaders = [1, 2, 3, 4, 5].map((tier) =>
    retrieveContentfulData<string>(`health_details_section.rx_tiers_static_values.rx_tiers.tier_${tier}`),
  );

  // Same shape as noOonCoverage: Contentful drives the copy, the literal is only a fallback for a
  // field that has not been published yet (<Text /> would render an empty cell instead).
  const rxInNetworkOnlyText =
    retrieveContentfulData<string>('health_section.compare_plan_text.compareModal.noRxOonCoverage') ||
    'Rx only covered in-network';
  const rxSeePlanDocumentsText =
    retrieveContentfulData<string>('health_section.compare_plan_text.compareModal.noShowRxOonCoverage') ||
    'See plan documents for details';

  const firstPlan = plans[0] || null;
  const secondPlan = plans[1] || null;
  const thirdPlan = plans[2] || null;
  const comparedPlans = [firstPlan, secondPlan, thirdPlan];

  const getRxCoverage = (plan: Plan) =>
    plan.getRxCoverageForTier(getSelectedTier ? getSelectedTier(plan) : 'in_network');

  // A plan "has data" for a tier when the coverage it displays (which depends on the selected
  // network tier) has retail or mail-order pricing for that tier.
  // Single source of truth: drives both the per-plan "N/A" cell (renderPlanInfo) and
  // whether the tier row renders at all (renderRxFields), so the two can't drift apart.
  const planHasRxTierData = (plan: Plan | null, tierKey: keyof RxTierNames) => {
    if (!plan) return false;
    const rxCoverage = getRxCoverage(plan);
    if (rxCoverage.display !== 'tiers') return false;
    return !!(rxCoverage.tierGroup?.[tierKey]?.retail || rxCoverage.tierGroup?.[tierKey]?.mail_order);
  };

  const renderPlanInfo = (plan: Plan | null, tierKey: keyof RxTierNames) => {
    if (!plan) return null;

    const rxCoverage = getRxCoverage(plan);

    if (rxCoverage.display !== 'tiers') {
      // The message is repeated on every tier row rather than shown once: an empty cell falls back
      // to a dash, which reads as missing data instead of "this plan has no out-of-network Rx".
      return (
        <Paragraph size="small" data-testid="compare-rx-no-oon-coverage">
          {rxCoverage.display === 'inNetworkOnly' ? rxInNetworkOnlyText : rxSeePlanDocumentsText}
        </Paragraph>
      );
    }

    const { tierGroup, isOutOfNetwork } = rxCoverage;

    const RxTierName = getRxTierName({
      retrieveContentfulData,
      planId: plan.planId,
      tierKey,
      tierGroup,
      isOutOfNetwork,
    });

    const retailTier = tierGroup?.[tierKey]?.retail;
    const mailTier = tierGroup?.[tierKey]?.mail_order;

    if (!planHasRxTierData(plan, tierKey)) {
      return <>N/A</>;
    }

    const retailConfig = getRxFieldAndVars(retailTier);
    const mailConfig = getRxFieldAndVars(mailTier);

    const isNoCoverage =
      (retailConfig?.isText && mailConfig?.isText) ||
      (retailConfig?.isText && !mailConfig) ||
      (mailConfig?.isText && !retailConfig);

    // Skipped when neither Contentful nor the API names the tier: an empty paragraph still takes
    // up its margin and would leave a gap above the values.
    const tierNameLabel = RxTierName ? (
      <Paragraph data-testid="compare-rx-tier-name" weight="bold" size="small" color="--text-black">
        {RxTierName}
      </Paragraph>
    ) : null;

    if (isNoCoverage) {
      /* Show the tier name with a single "Not Covered" when:
      - Both Retail and Mail Order are not covered
      - Only Retail available and it's not covered
      - Only Mail Order available and it's not covered
      When only one side is not covered, the render below shows both labels instead. */
      return (
        <>
          {tierNameLabel}
          <Paragraph className="not-covered" size="small">
            <Text field={'health_details_section.rx_tiers_static_values.no_coverage' as TextField} />
          </Paragraph>
        </>
      );
    }

    return (
      <>
        {tierNameLabel}
        <div data-testid="compare-rx-tier-values">
          {retailConfig && (
            <>
              <H6>
                <Text field={'health_details_section.rx_tiers_static_values.retail' as TextField} />
              </H6>
              {retailConfig.isText ? (
                <Paragraph size="small">
                  <Text field={retailConfig.field as TextField} />
                </Paragraph>
              ) : (
                <>
                  <Paragraph size="small">
                    <RichText
                      inlineParagraphs
                      field={retailConfig.field as TextField}
                      vars={retailConfig.vars}
                    />
                  </Paragraph>
                  {isDeductibleWaived(retailTier) && (
                    <Paragraph size="small">
                      <Text field={isDeductibleWaived(retailTier) as TextField} />
                    </Paragraph>
                  )}
                </>
              )}
            </>
          )}
          {mailConfig && (
            <>
              <H6>
                <Text field={'health_details_section.rx_tiers_static_values.mail_order' as TextField} />
              </H6>
              {mailConfig.isText ? (
                <Paragraph size="small">
                  <Text field={mailConfig.field as TextField} />
                </Paragraph>
              ) : (
                <>
                  <Paragraph size="small">
                    <RichText inlineParagraphs field={mailConfig.field as TextField} vars={mailConfig.vars} />
                  </Paragraph>
                  {isDeductibleWaived(mailTier) && (
                    <Paragraph size="small">
                      <Text field={isDeductibleWaived(mailTier) as TextField} />
                    </Paragraph>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </>
    );
  };

  const renderRxFields = () => {
    // Only render a tier row when at least one compared plan has coverage data
    // (retail or mail order) for that tier; otherwise every column would show "N/A".
    const visibleTiers = ([1, 2, 3, 4, 5] as const).filter((tier) => {
      const tierKey = `rx_tier_${tier}` as keyof RxTierNames;
      return comparedPlans.some((plan) => planHasRxTierData(plan, tierKey));
    });

    // A plan that isn't showing tiers renders a stand-in message for its whole column, so keep
    // one row for that message even when no compared plan has tier data to show.
    const hasStandInMessage = comparedPlans.some((plan) => !!plan && getRxCoverage(plan).display !== 'tiers');
    const tiersToRender = visibleTiers.length || !hasStandInMessage ? visibleTiers : [1 as const];

    return tiersToRender.map((tier) => {
      const tierKey = `rx_tier_${tier}` as keyof RxTierNames;
      return (
        <CompareTableRow
          rowHeader={rxHeaders[tier - 1]}
          columnOne={renderPlanInfo(firstPlan, tierKey)}
          columnTwo={renderPlanInfo(secondPlan, tierKey)}
          columnThree={renderPlanInfo(thirdPlan, tierKey)}
          key={`compare-table-${tier}`}
          keyHint={`compare-table-${tier}`}
          hideTooltip
          alignStart
        />
      );
    });
  };

  return <CompareTable tableHeader={header}>{renderRxFields()}</CompareTable>;
};
